import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErpTable, MetricCard, Td, Th } from '@/components/erp/primitives'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { fmtNumber } from '@/shared/lib/format'
import { useElectionContext } from '@/shared/election/context'
import { NoElectionSelected } from '@/shared/election/ElectionSelector'
import { seatBoard, type SeatResult } from './api'
import { CountedStatuses, ReturnStatusChip, SEAT_ORDER, seatLabel } from './vocab'
import { PlaceBreakdown } from './PlaceBreakdown'

/**
 * One race, candidate by candidate, then place by place.
 *
 * ## The breakdown is one level down, and comes from the database
 *
 * A national presidential view splits by county; a county view by constituency. Each is ONE
 * grouped query against the ltree index — the client asks for a level, it does not assemble
 * one out of station rows.
 *
 * ## Widening the statuses is offered, and never silent
 *
 * The default counts verified and certified returns only. A desk watching a count come in
 * legitimately wants to see what has been entered but not yet checked — so the toggle exists,
 * and the line under the figures changes when it is used.
 */
export function SeatDashboardPage() {
  const { electionId } = useElectionContext()
  const [params, setParams] = useSearchParams()
  const seat = params.get('seat') ?? 'PRESIDENT'
  const includeEntered = params.get('entered') === '1'

  const [result, setResult] = useState<SeatResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const statuses = includeEntered
    ? ['SUBMITTED', 'EXTRACTED', 'VERIFIED', 'CERTIFIED']
    : null

  // seat AND the status widening are both in the array: changing either must refetch, and
  // the electionId trap applies here as everywhere in this module.
  const load = useCallback(async () => {
    if (!electionId) { setResult(null); return }
    try {
      setError(null)
      setResult(null)
      setResult(await seatBoard({ electionId, seatType: seat, statuses }))
    } catch (err) {
      setError(explain(err))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [electionId, seat, includeEntered])

  useEffect(() => { void load() }, [load])

  if (!electionId) return <NoElectionSelected what="A seat dashboard" />

  const setSeat = (code: string) =>
    setParams((p) => { p.set('seat', code); return p }, { replace: true })

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {SEAT_ORDER.map((code) => (
          <button key={code} type="button" onClick={() => setSeat(code)}
            className={cn('h-8 px-2.5 rounded-md border text-erp-xs',
              seat === code
                ? 'border-chocolate bg-chocolate-soft/40 text-chocolate font-medium'
                : 'border-input text-muted-foreground hover:text-foreground')}>
            {seatLabel(code)}
          </button>
        ))}
        <label className="ml-auto inline-flex items-center gap-1.5 text-erp-xs text-muted-foreground">
          <input type="checkbox" checked={includeEntered}
            onChange={(e) => setParams((p) => {
              if (e.target.checked) p.set('entered', '1')
              else p.delete('entered')
              return p
            }, { replace: true })} />
          Include returns that are entered but not verified
        </label>
      </div>

      <ErrorStrip message={error} className="rounded-md border" />

      {result === null && !error ? <LoadingBlock label="Counting…" /> : result && (
        <>
          <div className="grid gap-2"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
            <MetricCard label="Units reporting"
              value={result.unitsExpected != null
                ? `${fmtNumber(result.unitsReporting)} / ${fmtNumber(result.unitsExpected)}`
                : fmtNumber(result.unitsReporting)} />
            <MetricCard label="Valid votes" value={fmtNumber(result.validVotes)} />
            <MetricCard label="Rejected" value={fmtNumber(result.rejectedVotes)} />
            <MetricCard label="Registered" value={fmtNumber(result.registeredVoters)} />
            <MetricCard label="Turnout"
              value={result.turnoutPct == null ? '—' : `${result.turnoutPct}%`} />
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <CountedStatuses statuses={result.countedStatuses} />
            {result.statusBreakdown
              .filter((s) => !result.countedStatuses.includes(s.statusCode))
              .map((s) => (
                <span key={s.statusCode} className="inline-flex items-center gap-1 text-erp-xs">
                  <ReturnStatusChip code={s.statusCode} />
                  <span className="tabular-nums text-muted-foreground">
                    {fmtNumber(s.returns)} not counted
                  </span>
                </span>
              ))}
          </div>

          <ErpTable head={(
            <>
              <Th className="w-10 text-right">#</Th>
              <Th>Candidate</Th>
              <Th className="text-right">Votes</Th>
              <Th className="text-right">Share of valid</Th>
              <Th>Units</Th>
            </>
          )}>
            {result.candidates.length === 0 ? (
              <tr>
                <Td className="text-muted-foreground">
                  {result.statusBreakdown
                    .filter((s) => !result.countedStatuses.includes(s.statusCode))
                    .reduce((n, s) => n + s.returns, 0) > 0
                    ? 'No figures counted yet — returns are filed but not verified. Boards '
                      + 'count verified and certified returns only; the Returns tab lists '
                      + 'what is waiting.'
                    : 'No candidate figures counted yet.'}
                </Td>
              </tr>
            ) : result.candidates.map((c, i) => (
              <tr key={c.candidacyId}>
                <Td className="text-right tabular-nums text-muted-foreground">{i + 1}</Td>
                <Td>
                  {c.ballotName}
                  {c.independent && (
                    <span className="ml-1.5 text-erp-xs text-muted-foreground">Independent</span>
                  )}
                </Td>
                <Td className="text-right tabular-nums font-medium">{fmtNumber(c.votes)}</Td>
                <Td className="text-right tabular-nums">
                  {c.sharePct == null ? '—' : `${c.sharePct}%`}
                </Td>
                <Td className="tabular-nums text-muted-foreground">
                  {fmtNumber(c.unitsReporting)}
                </Td>
              </tr>
            ))}
          </ErpTable>
        </>
      )}

      <PlaceBreakdown electionId={electionId} seatType={seat} statuses={statuses} />
    </div>
  )
}
